// Role → landing-route table, and the decision core for what the site root
// ("/") should do on a given device.
//
// WHY THIS EXISTS
// ---------------
// `/` renders the trader welcome screen (app/page.tsx). It is a client
// component, but Next.js still SSRs it, so the "Welcome to … / Login to …"
// markup is in the initial HTML and paints immediately. The auth decision
// used to live in a `useEffect`, which only runs AFTER hydration — 300ms on
// desktop, 1–2s on a mid-range Android device. Every logged-in user who hit
// `/` saw a full welcome screen first and got bounced afterwards.
//
// That is most visible in the APK: Capacitor's `server.url` is a bare origin
// (scripts/build-apk.sh), so the WebView loads `/` on EVERY cold start.
//
// The fix is a pre-paint inline script in the root layout <head> that calls
// `resolveRootRoute()` before the body is parsed. This module is that script's
// single source of truth — the same table is also used by app/page.tsx for the
// post-hydration cookie-recovery path, so the two can never drift.
//
// Everything here is pure and dependency-free: the layout serialises the two
// maps into an ES5 inline script, and the tests exercise `resolveRootRoute`
// directly.

/** Roles as they are persisted in `localStorage.user.role` by the login pages. */
export type Role = "trader" | "admin" | "broker" | "super_admin"

/** Where a user with a LIVE session belongs when they land on "/". */
export const ROLE_HOME: Record<string, string> = {
  super_admin: "/superadmin",
  admin: "/admin",
  broker: "/admin",
  trader: "/trade/dashboard",
}

/**
 * Where a user with NO live session — but a known prior role on this device —
 * belongs when they land on "/".
 *
 * Trader maps to `/trade` rather than `/trade/login` on purpose: `/trade` is a
 * routing gate that probes the httpOnly session cookie via /api/auth/me first,
 * so a trader whose localStorage was evicted under Android storage pressure
 * (but whose 7-day cookie survived) is recovered into the dashboard instead of
 * being asked to log in again. Admin/super-admin have no equivalent gate, so
 * they go straight to their login pages.
 */
export const ROLE_LOGIN: Record<string, string> = {
  super_admin: "/superadmin/login",
  admin: "/admin/login",
  broker: "/admin/login",
  trader: "/trade",
}

/** Fallback for "this device has been here before, but we don't know as whom". */
export const DEFAULT_RETURNING_ROUTE = "/trade"

/** localStorage keys the guard reads/writes. */
export const LS_USER = "user"
export const LS_LAST_ROLE = "last_role"
export const LS_HAS_SEEN_WELCOME = "has_seen_welcome"

/** Query flag that forces the welcome screen to render (support, QA, "Back" links). */
export const FORCE_WELCOME_PARAM = "welcome"

export interface RootRouteInput {
  /** Role from a live session hint (`localStorage.user`), or null. */
  role: string | null
  /**
   * Role cached from any previous login on this device (`localStorage.last_role`).
   * Deliberately NOT cleared on logout — it is what lets a returning-but-logged-out
   * broker land on /admin/login instead of the trader welcome screen.
   */
  lastRole: string | null
  /** Device has already been shown the welcome screen at least once. */
  hasSeenWelcome: boolean
  /** `?welcome=1` present — caller explicitly wants the welcome screen. */
  forceWelcome: boolean
}

/**
 * Decide what "/" should do.
 *
 * @returns the path to redirect to, or `null` to render the welcome screen.
 *
 * Precedence:
 *   1. `?welcome=1`            → always render the welcome screen.
 *   2. live session role       → that role's home.
 *   3. cached prior role       → that role's login/gate.
 *   4. welcome already seen    → the generic trader gate.
 *   5. otherwise               → render the welcome screen (genuine first run).
 *
 * Rule 5 is the "first time case: whatever URL loads, load that only" case —
 * a brand-new device sees the welcome screen exactly once, and never again.
 */
/**
 * Is the caller explicitly asking for the welcome screen (`?welcome=1`)?
 *
 * Shared by the pre-paint guard in app/layout.tsx and by app/page.tsx's
 * post-hydration effect — both must agree, otherwise the guard correctly stays
 * on "/" and then the effect redirects away a moment later, which is the same
 * flash in reverse.
 *
 * @param search `location.search`, with or without the leading "?".
 */
export function isForceWelcome(search: string | null | undefined): boolean {
  const qs = (search || "").replace(/^\?/, "")
  return `&${qs}`.indexOf(`&${FORCE_WELCOME_PARAM}=`) !== -1
}

export function resolveRootRoute(input: RootRouteInput): string | null {
  if (input.forceWelcome) return null

  if (input.role) {
    const home = ROLE_HOME[input.role]
    if (home) return home
  }

  if (input.lastRole) {
    const login = ROLE_LOGIN[input.lastRole]
    if (login) return login
  }

  if (input.hasSeenWelcome) return DEFAULT_RETURNING_ROUTE

  return null
}

/**
 * Post-login / post-recovery destination for a known role.
 * Falls back to the trader dashboard, matching the pre-existing behaviour of
 * app/page.tsx (which treated every non-admin role as a trader).
 */
export function roleHome(role: string | null | undefined): string {
  return (role && ROLE_HOME[role]) || ROLE_HOME.trader
}

/**
 * Home route for a role that is definitively NOT a trader — otherwise `null`.
 *
 * Answers the "the session cookie is valid, but it isn't a trader's" case that
 * every gate inside the trader frame (`/`, `/trade`, the trade client layout,
 * `/trade/login`) has to handle after probing /api/auth/me. Those gates used to
 * treat it as "not logged in" and render the trader login page, which is how a
 * logged-in admin ended up staring at a trader login form while their session
 * was perfectly alive.
 *
 * Deliberately NOT `roleHome()`: that falls back to `/trade/dashboard` for an
 * unrecognised role, and a trader-frame gate redirecting to a trader route
 * re-enters the same gate — an unknown role would spin forever. Returning
 * `null` for "trader or unrecognised" lets each caller keep its existing
 * fall-through (render the login form / stay put), which is the safe default.
 */
export function nonTraderHome(role: string | null | undefined): string | null {
  if (!role || role === "trader") return null
  return ROLE_HOME[role] || null
}
